import { Client, Estimate, Facility, Payor, PayorPlan, Plan, QuoteMethod, RateAsPayorMap } from '@finpay/estimation-types';
import { AppDispatch } from '../../shared/state/store';
import { estimatorSlice } from '../../shared/state/estimator-slice';
import { Dispatch, AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { PayerRow } from '../../admin-configuration/models/payers';
import { Vob } from '@finpay/estimation-types';
import { admissionsAdvisorService } from '../../admissions-advisor/services/admissions-advisor-services';
import { showErrorStatus } from '../../security/state/user-slice';
import { loadSavedEstimatesListv3, saveSelectedEstimate, saveSelectedEstimateSelfPay } from '../../shared/state/estimator-thunk';
import _ from 'lodash';

// Define interfaces for the facility payer data structure
// TODO: use vob types
interface FacilityPayorPlan {
  cfgPayorPlanId: number;
  facilityPayorPlanId: number;
  facilityPayorPlanName: string;
  facilityPayorPlanCode?: string;
}

// TODO: use vob types
interface FacilityPayer {
  cfgPayorId: number;
  facilityPayorId: number;
  facilityPayorName: string;
  facilityPayorCode?: string;
  inNetwork: boolean;
  facilityPayorPlans?: FacilityPayorPlan[];
}

/**
 * Loads facility payers and sets the rateAsPayorPlan based on VOB data
 * 
 * @param client Client information
 * @param facility Facility information
 * @param dispatch Redux dispatch function
 * @param masterPayors Map of master payors
 * @param payor Payor information
 * @param plan Plan information
 * @param rateAsPayorMap Map of rate as payor information
 * @returns Promise that resolves when the operation is complete
 */
export const loadAndSetRateAsPayorPlan = async (
  client: Client,
  facility: Facility,
  dispatch: Dispatch<AnyAction> | ThunkDispatch<any, any, AnyAction>,
  masterPayors: Map<number, PayerRow>,
  payor: Payor,
  plan: Plan,
  rateAsPayorMap: Record<number, RateAsPayorMap[]>,
  estimate?: Estimate,
  rateAsPayorPlan?: PayorPlan,
  selectedVob?: Vob | undefined,
  updatedEstimate?: Estimate,
  newVobId?: string | number,
): Promise<void> => {
  if (!facility?.facilityId) return;

  try {
    const response = await admissionsAdvisorService.getEstimatorFacilityLocPayers(
      -2,
      Number(facility.facilityId)
    );

    if (!response.hasErrors && response.entity?.facilityPayors) {
      findAndSetRateAsPayorPlan({
        facilityPayers: response.entity.facilityPayors,
        client,
        facility,
        payor,
        plan,
        dispatch,
        rateAsPayorMap,
        masterPayors,
        estimate,
        rateAsPayorPlan,
        selectedVob,
        updatedEstimate,
        newVobId
      });
    }
  } catch (error) {
    console.error('Error in loadAndSetRateAsPayorPlan:', error);
  }
};


export const loadDataFromSavedRateAsPayorPlan = async (
  facility: Facility,
  savedRateAsPayorPlan: { facilityPlanId?: number; plan?: { facilityPlanId?: number }; [key: string]: any },
  dispatch: Dispatch<AnyAction> | ThunkDispatch<any, any, AnyAction>,
  masterPayors: Map<number, PayerRow>
): Promise<void> => {
  const facilityPlanId = savedRateAsPayorPlan?.facilityPlanId ?? savedRateAsPayorPlan?.plan?.facilityPlanId;
  if (!facility?.facilityId || facilityPlanId == null) return;

  try {
    const response = await admissionsAdvisorService.getEstimatorFacilityLocPayers(
      -2,
      Number(facility.facilityId)
    );
    if (response.hasErrors || !response.entity?.facilityPayors) return;

    const facilityPayers: FacilityPayer[] = response.entity.facilityPayors;
    for (const payor of facilityPayers) {
      const matchedPlan = payor.facilityPayorPlans?.find(
        (p: FacilityPayorPlan) => p.facilityPayorPlanId === facilityPlanId
      );
      if (matchedPlan) {
        const masterPayor = masterPayors.get(payor.cfgPayorId);
        const masterPlan = masterPayor?.payorPlans?.find(
          (p: any) => p.payorPlanId === matchedPlan.cfgPayorPlanId
        );
        const payorResult: PayorPlan = {
          payor: {
            payorId: payor.cfgPayorId,
            facilityPayorId: payor.facilityPayorId,
            payorName: masterPayor?.payorName,
            facilityPayorName: payor.facilityPayorName,
            inNetwork: payor.inNetwork
          },
          plan: {
            payorPlanId: matchedPlan.cfgPayorPlanId,
            facilityPlanId: matchedPlan.facilityPayorPlanId,
            planName: masterPlan?.planName,
            facilityPlanName: matchedPlan.facilityPayorPlanName
          },
          isOverridden: true
        };
        dispatch(estimatorSlice.actions.setRateAsPayorPlan(payorResult));

        const levelsOfCareResponse = await admissionsAdvisorService.getFacilityLevelsOfCare(
          Number(facility.facilityId),
          facilityPlanId
        );
        if (!levelsOfCareResponse.hasErrors && Array.isArray(levelsOfCareResponse.entity)) {
          const formedLevels = levelsOfCareResponse.entity.map((level: any) => ({
            ...level,
            levelOfCareId: level.facilityLevelOfCareId,
            levelOfCareCode: level.facilityLevelOfCareCode,
            levelOfCareName: level.facilityLevelOfCareName,
            vobClassificationId: level.vobClassificationId,
            sortOrder: level.sortOrder || 0
          }));
          dispatch(estimatorSlice.actions.setFacilityLevelsOfCareList(formedLevels));
        } else {
          dispatch(estimatorSlice.actions.setFacilityLevelsOfCareList(null));
        }
        return;
      }
    }
    dispatch(estimatorSlice.actions.setRateAsPayorPlan(null));
    dispatch(estimatorSlice.actions.setFacilityLevelsOfCareList(null));
  } catch (error) {
    console.error('Error in loadDataFromSavedRateAsPayorPlan:', error);
  }
};


export const loadDataFromRateAsPayorPlanInNewFacility = async (
  facility: Facility,
  existingRateAsPayorPlan: PayorPlan,
  dispatch: Dispatch<AnyAction> | ThunkDispatch<any, any, AnyAction>,
  masterPayors: Map<number, PayerRow>
): Promise<boolean> => {
  const payorId = existingRateAsPayorPlan?.payor?.payorId;
  const payorPlanId = existingRateAsPayorPlan?.plan?.payorPlanId;
  if (!facility?.facilityId || payorId == null || payorPlanId == null) return false;

  try {
    const response = await admissionsAdvisorService.getEstimatorFacilityLocPayers(
      -2,
      Number(facility.facilityId)
    );
    if (response.hasErrors || !response.entity?.facilityPayors) return false;

    const facilityPayers: FacilityPayer[] = response.entity.facilityPayors;
    const payor = facilityPayers.find((p: FacilityPayer) => p.cfgPayorId === payorId);
    const matchedPlan = payor?.facilityPayorPlans?.find(
      (p: FacilityPayorPlan) => p.cfgPayorPlanId === payorPlanId
    );
    if (!payor || !matchedPlan) return false;

    const masterPayor = masterPayors.get(payor.cfgPayorId);
    const masterPlan = masterPayor?.payorPlans?.find(
      (p: any) => p.payorPlanId === matchedPlan.cfgPayorPlanId
    );
    const payorResult: PayorPlan = {
      payor: {
        payorId: payor.cfgPayorId,
        facilityPayorId: payor.facilityPayorId,
        payorName: masterPayor?.payorName,
        facilityPayorName: payor.facilityPayorName,
        inNetwork: payor.inNetwork
      },
      plan: {
        payorPlanId: matchedPlan.cfgPayorPlanId,
        facilityPlanId: matchedPlan.facilityPayorPlanId,
        planName: masterPlan?.planName,
        facilityPlanName: matchedPlan.facilityPayorPlanName
      },
      isOverridden: true
    };
    dispatch(estimatorSlice.actions.setRateAsPayorPlan(payorResult));

    const levelsOfCareResponse = await admissionsAdvisorService.getFacilityLevelsOfCare(
      Number(facility.facilityId),
      matchedPlan.facilityPayorPlanId
    );
    if (!levelsOfCareResponse.hasErrors && Array.isArray(levelsOfCareResponse.entity)) {
      const formedLevels = levelsOfCareResponse.entity.map((level: any) => ({
        ...level,
        levelOfCareId: level.facilityLevelOfCareId,
        levelOfCareCode: level.facilityLevelOfCareCode,
        levelOfCareName: level.facilityLevelOfCareName,
        vobClassificationId: level.vobClassificationId,
        sortOrder: level.sortOrder || 0
      }));
      dispatch(estimatorSlice.actions.setFacilityLevelsOfCareList(formedLevels));
    } else {
      dispatch(estimatorSlice.actions.setFacilityLevelsOfCareList(null));
    }
    return true;
  } catch (error) {
    console.error('Error in loadDataFromRateAsPayorPlanInNewFacility:', error);
    return false;
  }
};

/**
 * Finds and sets the rateAsPayorPlan based on VOB data and facility payers
 * 
 * @param params Configuration object
 * @param params.facilityPayers List of facility payers
 * @param params.vob VOB data containing payor and plan information
 * @param params.dispatch Redux dispatch function
 * @returns The found PayorPlan object or null if no match was found
 */
export const findAndSetRateAsPayorPlan = async (params: {
  facilityPayers: FacilityPayer[];
  client: Client;
  facility: Facility;
  payor: Payor;
  plan: Plan;
  dispatch: Dispatch<AnyAction> | ThunkDispatch<any, any, AnyAction>;
  rateAsPayorMap: Record<number, RateAsPayorMap[]>;
  masterPayors: Map<number, PayerRow>;
  estimate?: Estimate;
  rateAsPayorPlan?: PayorPlan;
  selectedVob?: Vob | undefined;
  updatedEstimate?: Estimate;
  newVobId?: string | number,
}): Promise<PayorPlan | null> => {
  console.log(
    "%cFinding and Setting Rate as Payor Plan",
    "color: #4CAF50; font-weight: bold; font-size: 12px; background: #E8F5E9; padding: 4px 8px; border-radius: 4px;"
  );
  const { facilityPayers, client, facility, payor, plan, dispatch, rateAsPayorMap, masterPayors, estimate, rateAsPayorPlan, selectedVob, updatedEstimate, newVobId } = params;
  let payorResult: PayorPlan | null = null;

  if (!client || !facility || !payor || !plan) {
    return null;
  }

  try {
    // Match first if payor & plan provided
    if (payor?.payorId && plan?.payorPlanId) {
      const clientRateAsPayorMap = rateAsPayorMap[client.clientId];

      const payorPlan = findMatchedPayorPlan(
        facilityPayers,
        payor,
        plan,
        clientRateAsPayorMap,
        masterPayors
      );

      if (payorPlan) {
        payorResult = payorPlan;

        // Clean up empty properties
        if (!payorResult.payor.facilityPayorName)
          delete payorResult.payor.facilityPayorName;
        if (!payorResult.plan.facilityPlanName)
          delete payorResult.plan.facilityPlanName;
      }
    }

    // Fallback to Average Out-of-Network
    if (!payorResult) {
      const averageOutOfNetworkPayor = findAverageOutOfNetworkPayor(facilityPayers, masterPayors);

      if (
        averageOutOfNetworkPayor?.facilityPayorPlans?.length
      ) {
        const firstPlan = averageOutOfNetworkPayor.facilityPayorPlans[0];
        const matchedMasterPayor = masterPayors.get(averageOutOfNetworkPayor.cfgPayorId);
        const matchedMasterPlan = matchedMasterPayor?.payorPlans?.find(
          (p: any) => p.payorPlanId === firstPlan.cfgPayorPlanId
        );

        payorResult = {
          payor: {
            payorId: averageOutOfNetworkPayor.cfgPayorId,
            facilityPayorId: averageOutOfNetworkPayor.facilityPayorId,
            payorName: matchedMasterPayor?.payorName,
            facilityPayorName: averageOutOfNetworkPayor.facilityPayorName,
            inNetwork: averageOutOfNetworkPayor.inNetwork
          },
          plan: {
            payorPlanId: firstPlan.cfgPayorPlanId,
            facilityPlanId: firstPlan.facilityPayorPlanId,
            planName: matchedMasterPlan?.planName,
            facilityPlanName: firstPlan.facilityPayorPlanName
          },
          isOverridden: false
        };

        if (!averageOutOfNetworkPayor.facilityPayorName)
          delete payorResult.payor.facilityPayorName;
        if (!firstPlan.facilityPayorPlanName)
          delete payorResult.plan.facilityPlanName;
      }
    }

    let levelsOfCareResponse;
    if (payorResult?.plan?.facilityPlanId) {
      levelsOfCareResponse = await admissionsAdvisorService.getFacilityLevelsOfCare(
        facility?.facilityId!,
        payorResult.plan.facilityPlanId
      );

      if (!levelsOfCareResponse.hasErrors && Array.isArray(levelsOfCareResponse.entity)) {
        const formedLevels = levelsOfCareResponse.entity
        
          .map((level: any) => ({
            ...level,
            levelOfCareId: level.facilityLevelOfCareId,
            levelOfCareCode: level.facilityLevelOfCareCode,
            levelOfCareName: level.facilityLevelOfCareName,
            vobClassificationId: level.vobClassificationId,
            sortOrder: level.sortOrder || 0
          }));
        dispatch(estimatorSlice.actions.setFacilityLevelsOfCareList(formedLevels));
      } else {
        // API call failed or returned errors
        console.error('Failed to load facility levels of care:', levelsOfCareResponse);
        if (dispatch) {
          dispatch(showErrorStatus('Failed to load levels of care. Please try again.')); // check with RD on relevant error message
          dispatch(estimatorSlice.actions.setFacilityLevelsOfCareList(null));
        }
      }
    } else if (!payorResult) {
      if (dispatch) {
        dispatch(estimatorSlice.actions.setFacilityLevelsOfCareList(null));
      }
    }
    dispatch(estimatorSlice.actions.setRateAsPayorPlan(payorResult));

    // the rateAsPayorPlan is always Average OON
    const changingFromPrivatePay = (estimate?.isPrivatePay && (updatedEstimate?.isPrivatePay !== estimate.isPrivatePay));
    // Creating new boolean for new estimate private pay / self pay to use for compare
    const isNewEstimateSelfPay = updatedEstimate?.isPrivatePay || updatedEstimate?.isSelfPay;
    // choosing which facilityId to compare based on self/private pay boolean
    const comparisonFacilityId = isNewEstimateSelfPay
        ? estimate?.facilityId
        : selectedVob?.facility?.facilityId;
    // setting detect change in facility appropriately
    const changingFromFacility = comparisonFacilityId !== facility?.facilityId;
    const selectedVobId = newVobId;

    // Detecting a change between Current RateAsPayorPlan and New RateAsPayorPlan
    if ((rateAsPayorPlan && rateAsPayorPlan?.payor?.payorId !== payorResult?.payor?.payorId) || changingFromPrivatePay || changingFromFacility) {
      // check to see if the estimate has LOC
      if (estimate?.facilityLevelOfCare?.length) {
        await handleLOCMatching({
          estimate,
          rateAsPayorPlan,
          payorResult,
          levelsOfCareResponse,
          selectedVobId,
          client,
          facility,
          dispatch,
          updatedEstimate
        });
      }
    }

    return payorResult;

  } catch (error) {
    console.error('Error finding rateAsPayorPlan:', error);
    return null;
  }
};

// ---- LOC Matching helper used by LoadAndSetPayorPlan----
export async function handleLOCMatching({
  estimate,
  rateAsPayorPlan,
  payorResult,
  levelsOfCareResponse,
  selectedVobId,
  dispatch,
  updatedEstimate,
}: {
  estimate: Estimate;
  rateAsPayorPlan: any | null;
  payorResult: any | null;
  levelsOfCareResponse: any;
  selectedVobId:  string | number | undefined,
  client: Client;
  facility: Facility;
  dispatch: AppDispatch;
  updatedEstimate: any;
}): Promise<{ matchedLOCs: any[]; missingLOCs: any[] }> {
  const matchedLOCs: any[] = [];
  const missingLOCs: any[] = [];

  if (Array.isArray(estimate?.facilityLevelOfCare) && estimate.facilityLevelOfCare.length) {
    const levels = Array.isArray(levelsOfCareResponse?.entity) ? levelsOfCareResponse!.entity : [];  

    for (const loc of estimate?.facilityLevelOfCare ?? []) {
      const found = levels.find((lvl: any) => lvl.cfgLevelOfCareId === loc.cfgLevelOfCareId);
    
      if (!found) {
        missingLOCs.push(loc);
        continue;
      }
    
      let merged = loc;
    
      switch (updatedEstimate.quoteMethod) {
        case QuoteMethod.SCA: {
          merged = { ...found };
          break;
        }
      
        case QuoteMethod.MANUAL: {
          merged = {
            ...found,
            pdrRate: loc.pdrRate,
            losDays: loc.losDays,
          };
          break;
        }
      
        case QuoteMethod.AVGLOS: {
          merged = {
            ...found,
            losDays: found.losDays,
            pdrRate: found.pdrRate,
          };
          break;
        }
      
        case QuoteMethod.RLOS: {
          merged = {
            ...found,
            losDays: found.recommendedLos,
            pdrRate: found.pdrRate,
          };
          break;
        }
      
        default: {
          // Fallback: keep found as truth source
          merged = { ...found };
          break;
        }

      }
      matchedLOCs.push(merged);
    }

    // check to see if quote method is Single 
    if (
      updatedEstimate.quoteMethod === QuoteMethod.SCA ||
      updatedEstimate.quoteMethod === QuoteMethod.MANUAL
    ) {
      // Build quick lookup from old estimate LOCs by cfgLevelOfCareId
      const prevById = new Map<number, any>(
        estimate.facilityLevelOfCare.map((loc: any) => [loc.cfgLevelOfCareId, loc])
      );
    
      const isSCA = updatedEstimate.quoteMethod === QuoteMethod.SCA;
      const isManual = updatedEstimate.quoteMethod === QuoteMethod.MANUAL;

      for (const lvl of matchedLOCs) {
        const prev = prevById.get(lvl.cfgLevelOfCareId);
        if (!prev) continue;
    
        // SCA: copy rate
        if (isSCA && prev.pdrRate !== undefined) {
          lvl.pdrRate = prev.pdrRate;
        }
    
        // SCA or MANUAL: copy days
        if ((isSCA || isManual) && prev.losDays !== undefined) {
          lvl.losDays = prev.losDays;
        }
      }
    }

    // Only persist if we actually have something to set
    // if (matchedLOCs.length > 0) {
    const {
      estimateId,
      createUserId,
      createDt,
      lastUpdateUserId,
      lastUpdateDt,
      summary,
      crossOverSummary,
      priorCareSummary,
      // rateAsPayorPlan,
      ...rest
    } = updatedEstimate;

    const putEstimateBody: Estimate = {
      ...rest,
      patientId: updatedEstimate.patientId!,
      vobId: selectedVobId,
      anticipatedAdmitDate: updatedEstimate.anticipatedAdmitDate,
      description: updatedEstimate.description,
      isPlanYearCrossover: updatedEstimate.isPlanYearCrossover,
      isSelfPay: updatedEstimate.isSelfPay,
      isPrivatePay: updatedEstimate.isPrivatePay,
      facilityLevelOfCare: matchedLOCs.length > 0 ? matchedLOCs : [],
      ...(payorResult && { rateAsPayorPlan: payorResult })
    };
    if (putEstimateBody.rateAsPayorPlan == null) {
      delete putEstimateBody.rateAsPayorPlan;
    }

    const saveEstimate =
    updatedEstimate.isSelfPay || updatedEstimate?.isPrivatePay
        ? saveSelectedEstimateSelfPay({ estimateId: estimate.estimateId, estimate: putEstimateBody })
        : saveSelectedEstimate({ estimateId: estimate.estimateId, estimate: putEstimateBody });

    await dispatch(saveEstimate);
    await dispatch(
        loadSavedEstimatesListv3({
            paramId: -2,
            patientId: Number(updatedEstimate.patientId!),
        })
    );

    if (missingLOCs.length > 0) {
      const missingNames = missingLOCs
        .map(loc => loc.facilityLevelOfCareName || loc.facilityPayorName || 'Unknown LOC')
        .join(', ');
    
      const message = `The following Levels of Care could not be matched and will be removed from the Estimate: ${missingNames}`;
    
      dispatch(showErrorStatus(message));
    }
  }

  return { matchedLOCs, missingLOCs };
}


/*
 matches the vob payor/plan to one of the facilities payor/plans
 */
export const findMatchedPayorPlan = (
  facilityPayors: FacilityPayer[],
  vobPayor: Payor,
  vobPlan: Plan,
  payorMap: RateAsPayorMap[],
  masterPayors: Map<number, PayerRow>
): PayorPlan | null => {
  if (facilityPayors && vobPlan?.facilityPlanId) {
    for (const facilityPayer of facilityPayors) {
      const matchedPlan = facilityPayer.facilityPayorPlans?.find(
        (plan: FacilityPayorPlan) => plan.facilityPayorPlanId === vobPlan.facilityPlanId
      );
      if (matchedPlan) {
        const masterPayor = masterPayors?.get(facilityPayer.cfgPayorId);
        const masterPlan = masterPayor?.payorPlans?.find(
          (p: any) => p.payorPlanId === matchedPlan.cfgPayorPlanId
        );
        return {
          payor: {
            payorId: facilityPayer.cfgPayorId,
            facilityPayorId: facilityPayer.facilityPayorId,
            payorName: masterPayor?.payorName,
            facilityPayorName: facilityPayer.facilityPayorName,
            inNetwork: facilityPayer.inNetwork
          },
          plan: {
            payorPlanId: matchedPlan.cfgPayorPlanId,
            facilityPlanId: matchedPlan.facilityPayorPlanId,
            planName: masterPlan?.planName,
            facilityPlanName: matchedPlan.facilityPayorPlanName
          },
          isOverridden: false
        };
      }
    }
  }
  
  if (!facilityPayors || !vobPayor?.payorId || !vobPlan?.payorPlanId) {
    return null;
  }

  let matchedFacilityPayor = facilityPayors.find(
    (facilityPayer: FacilityPayer) =>
      facilityPayer.cfgPayorId === vobPayor.payorId
  );

  // now match plan by id
  if (matchedFacilityPayor) {
    console.info(`Matched facility payor by id: ${JSON.stringify(matchedFacilityPayor)}`);
    const matchedFacilityPlan = matchedFacilityPayor.facilityPayorPlans?.find(
        (facilityPayer: FacilityPayorPlan) =>
            facilityPayer.cfgPayorPlanId === vobPlan.payorPlanId
    );

    if (matchedFacilityPlan) {
      return {
        payor: {
          payorId: matchedFacilityPayor.cfgPayorId,
          facilityPayorId: matchedFacilityPayor.facilityPayorId,
          payorName: masterPayors.get(matchedFacilityPayor.cfgPayorId)?.payorName,
          facilityPayorName: matchedFacilityPayor.facilityPayorName,
          inNetwork: matchedFacilityPayor.inNetwork
        },
        plan: {
          payorPlanId: matchedFacilityPlan.cfgPayorPlanId,
          facilityPlanId: matchedFacilityPlan.facilityPayorPlanId,
          planName: masterPayors.get(matchedFacilityPayor.cfgPayorId)?.payorPlans?.find(p => p.payorPlanId === matchedFacilityPlan.cfgPayorPlanId)?.planName,
          facilityPlanName: matchedFacilityPlan.facilityPayorPlanName
        },
        isOverridden: false
      };
    }
    return null;
  }

  console.info(`matching by name`);
  if (!matchedFacilityPayor && payorMap && masterPayors) {
      // use the rateAsPayorMap to match the vob payor to a facilityPayor.cfgPayorId
      const vobPayorName = masterPayors.get(vobPayor.payorId)?.payorName;
      console.info(`Matching payor by master name: ${vobPayorName}, masterPayors: ${JSON.stringify(masterPayors)}`);
      if (!vobPayorName) return null;

      // match exact payor name
      let matchedPayorMap = payorMap.find((t: RateAsPayorMap) => vobPayorName.toLowerCase() === t.payorName);
      if (matchedPayorMap) {
          console.info(`Matched payor map by exact name: ${JSON.stringify(matchedPayorMap)}`);
      }

      if (!matchedPayorMap) {
        // match payor names with wildcard
        matchedPayorMap = payorMap.find((t: RateAsPayorMap) => vobPayorName.toLowerCase().includes(t.payorName));
        if (matchedPayorMap) {
            console.info(`Matched payor map by wildcard: ${JSON.stringify(matchedPayorMap)}`);
        }
      }

      if (matchedPayorMap?.rateAsPayorPlan?.payor?.payorId) {
        matchedFacilityPayor = facilityPayors.find((t: FacilityPayer) => t.cfgPayorId === matchedPayorMap?.rateAsPayorPlan?.payor?.payorId);
      }
      // now match plan by id from matched rate as map
      if (matchedFacilityPayor && matchedPayorMap) {
        const matchedFacilityPlan = matchedFacilityPayor.facilityPayorPlans?.[0];
        if (matchedFacilityPlan) {
          return {
            payor: {
              payorId: matchedFacilityPayor.cfgPayorId,
              facilityPayorId: matchedFacilityPayor.facilityPayorId,
              payorName: masterPayors.get(matchedFacilityPayor.cfgPayorId)?.payorName,
              facilityPayorName: matchedFacilityPayor.facilityPayorName,
              inNetwork: matchedFacilityPayor.inNetwork
            },
            plan: {
              payorPlanId: matchedFacilityPlan.cfgPayorPlanId,
              facilityPlanId: matchedFacilityPlan.facilityPayorPlanId,
              planName: masterPayors.get(matchedFacilityPayor.cfgPayorId)?.payorPlans?.find(p => p.payorPlanId === matchedFacilityPlan.cfgPayorPlanId)?.planName,
              facilityPlanName: matchedFacilityPlan.facilityPayorPlanName
            },
            isOverridden: false
          };
        }
      }
  }
  return null;
};

/**
 * Finds the Average Out-of-Network payor from the facility payers list using master payors map
 * 
 * @param facilityPayers List of facility payers
 * @param masterPayors Map of master payors
 * @returns The Average Out-of-Network facility payor or undefined if not found
 */
export const findAverageOutOfNetworkPayor = (
  facilityPayers: FacilityPayer[], 
  masterPayors: Map<number, PayerRow>
): FacilityPayer | undefined => {
  if (!facilityPayers || !Array.isArray(facilityPayers) || !masterPayors) {
    return undefined;
  }
  
  // First find the master payor with name "Average Out-of-Network"
  // Convert Map to array to use find
  const averageOutOfNetworkEntry = Array.from(masterPayors.entries())
    .find(([_, payorData]) => payorData.payorName === "Average Out-of-Network");
    
  const averageOutOfNetworkMasterPayorId = averageOutOfNetworkEntry ? averageOutOfNetworkEntry[0] : undefined;
  
  // If we found a master payor, find the matching facility payor by cfgPayorId
  if (averageOutOfNetworkMasterPayorId) {
    const matchByMasterId = facilityPayers.find(
      (fp: FacilityPayer) => fp.cfgPayorId === averageOutOfNetworkMasterPayorId
    );
    
    if (matchByMasterId) {
      return matchByMasterId;
    }
  }
  
  // Fallback to name matching if no match by ID
  // try case-insensitive match
  const caseInsensitiveMatch = facilityPayers.find(
    (fp: FacilityPayer) => 
      fp.facilityPayorName && 
      fp.facilityPayorName.toLowerCase().includes("average out-of-network")
  );
  
  return caseInsensitiveMatch;
};
